<template>
	<div>
		<BaseCmsPage v-slot="{page}" @load="onPageLoad">
			<h1 style="display: none" v-if="page?.seo_title">{{ page.seo_title }}</h1>

			<CmsBenefits />

			<BaseCmsRotator
				:fetch="{code: 'homepage_rotator', limit: 4, response_fields: ['id','title','code','template','link_target_blank','url_without_domain','image_thumbs', 'image_4_thumbs', 'image_upload_path']}"
				:ga4="'home - rotator - '"
				@loadRotatorItems="loadRotatorItems"
				v-slot="{items}">
				<div v-if="items?.length" class="hp-rotator">
					<BaseUiSwiper
						name="hero"
						:options="{
							slidesPerView: 1,
							slidesPerGroup: 1,
							loop: true,
							effect: 'fade',
							fadeEffect: {
								crossFade: true
							},
							speed: 500,
							autoplay: {
								delay: 5000,
								disableOnInteraction: true
							},
							pagination: {
								enabled: true,
								clickable: true, 
								bulletClass: 'splide__slide progress-bar', 
								wrapperClass: 'progress-bars', 
								bulletElement: 'div', 
								bulletActiveClass: 'is-active progress-bar-active'
							}
						}">
						<BaseUiSwiperSlide v-for="(item, index) in items" :key="item.id">
							<NuxtLink
								v-if="item.url_without_domain"
								@click="gtm.gtmPromoClick(item.title, 'home - rotator - ', index, item.code, item.template)"
								:target="item.link_target_blank == 1 ? '_blank' : null"
								:to="item.url_without_domain"
								class="hp-rotator-slide link"
								:aria-label="item.title">
								<div v-if="item.image_upload_path" class="hp-rotator-img">
									<BaseUiImage
										:loading="index == 0 ? 'eager' : 'lazy'"
										:data="item.image_thumbs?.['width1480-height540-crop1']"
										default="/images/no-image-hero.webp"
										:picture="[{maxWidth: '760px', src: item.image_4_thumbs?.['width760-height397-crop1']?.thumb ? item.image_4_thumbs['width760-height397-crop1'].thumb : item.image_thumbs?.['width760-height397-crop1']?.thumb, default: '/images/no-image-hero-m.webp'}]" />
								</div>
							</NuxtLink>
							<div v-else class="hp-rotator-slide">
								<div v-if="item.image_upload_path" class="hp-rotator-img">
									<BaseUiImage
										loading="lazy"
										:data="item.image_thumbs?.['width1480-height540-crop1']"
										default="/images/no-image-hero.webp"
										:picture="[{maxWidth: '760px', src: item.image_4_thumbs?.['width760-height397-crop1']?.thumb ? item.image_4_thumbs['width760-height397-crop1'].thumb : item.image_thumbs?.['width760-height397-crop1']?.thumb, default: '/images/no-image-hero-m.webp'}]" />
								</div>
							</div>
						</BaseUiSwiperSlide>
					</BaseUiSwiper>
				</div>
			</BaseCmsRotator>

			<CmsFeaturedPromo />

			<BaseCmsRotator :fetch="{code: 'homepage_categories', limit: 40, response_fields: ['id','link_target_blank','url_without_domain','image_upload_path','title','image_thumbs']}" v-slot="{items}">
				<div v-if="items?.length" class="hp-categories wrapper">
					<div class="hp-section-header hp-categories-header">
						<div class="hp-title hp-categories-title"><BaseCmsLabel code="homepage_categories_title" /></div>
					</div>
					<div class="hp-categories-container">
						<NuxtLink v-for="item in items" :key="item.id" :target="item.link_target_blank == 1 ? '_blank' : null" :to="item.url_without_domain" class="hp-category">
							<span v-if="item.image_upload_path" class="img"><BaseUiImage :data="item.image_thumbs?.['width75-height75-crop1']" default="/images/no-image-100.webp" /></span>
							<span class="title">{{ item.title }}</span>
						</NuxtLink>
					</div>
				</div>
			</BaseCmsRotator>

			<BaseCatalogLists :fetch="{homepage: true, sort: 'homepage_position', limit: 5}" v-slot="{items: lists}">
				<template v-if="lists?.length">
					<div class="cwf wrapper">
						<div v-if="labels.get('bf_special_list_title') != 'bf_special_list_title'" class="cwf-header hp-section-header wrapper">
							<div class="cwf-title hp-title" v-html="labels.get('bf_special_list_title')"></div>
						</div>
						<template v-for="(list, index) in lists.filter(item => item.code.startsWith('bf_'))" :key="list.id">
							<CatalogProductsWidget :fetch="{mode: 'widget', list_code: list.code, list_title: list.title, sort: 'list_position', only_available: true, limit: 20, user_use_loyalty: userLoyaltyCode}" v-slot="{items}" :mode="list.code" @loadProductsWidget="loadProductsWidget">
								<ClientOnly>
									<div v-if="list?.id && items.length" class="cwf-list">
										<div class="cwf-tabs-content">
											<div v-if="list.main_image_3 && !mobileBreakpoint" class="cwf-promo">
												<NuxtLink :to="list.external_url ? list.external_url : list.url_without_domain">
													<BaseUiImage loading="eager" :data="list.main_image_3_thumbs?.['width240-height300-crop1']" default="/images/no-image-100.webp" />
												</NuxtLink>
											</div>
											<BaseUiSwiper
												:name="'cwf-' + index"
												:options="{
													slidesPerView: 2.5,
													slidesPerGroup: 2,
													spaceBetween: 15,
													loop: false,
													breakpoints: {
														0: {
															enabled: false
														},
														980: {
															slidesPerView: 1.5,
															slidesPerGroup: 1,
															spaceBetween: 15,
															enabled: true,
														},
														1200: {
															slidesPerView: 2.5,
															slidesPerGroup: 2,
															spaceBetween: 15,
															enabled: true,
														},
													}
												}">
												<BaseUiSwiperSlide v-if="list.main_image_3 && mobileBreakpoint" class="first-img">
													<NuxtLink :to="list.external_url ? list.external_url : list.url_without_domain">
														<BaseUiImage
															loading="eager"
															:data="list.main_image_3_thumbs?.['width240-height300-crop1']"
															default="/images/no-image-100.webp"
															:picture="[{maxWidth: '760px', src: list.main_image_4_thumbs?.['width760-height760']?.thumb ? list.main_image_4_thumbs['width760-height760'].thumb : list.main_image_3_thumbs?.['width240-height300-crop1']?.thumb, default: '/images/no-image-760.webp'}]" />
													</NuxtLink>
												</BaseUiSwiperSlide>
												<BaseUiSwiperSlide v-for="(item, index) in items" :key="item.id">
													<CatalogIndexEntry :item="item" :categoryListData="list" :index="index" mode="bfSlider" />
												</BaseUiSwiperSlide>
												<BaseUiSwiperSlide v-if="list.main_image_5" class="last-img">
													<NuxtLink :to="list.external_url ? list.external_url : list.url_without_domain">
														<BaseUiImage
															loading="eager"
															:data="list.main_image_5_thumbs?.['width240-height300-crop1']"
															default="/images/no-image-100.webp"
															:picture="[{maxWidth: '760px', src: list.main_image_6_thumbs?.['width760-height760']?.thumb ? list.main_image_6_thumbs?.['width760-height760']?.thumb : list.main_image_6_thumbs?.['width760-height760']?.thumb, default: '/images/no-image-760.webp'}]" />
													</NuxtLink>
												</BaseUiSwiperSlide>
											</BaseUiSwiper>
										</div>
									</div>
								</ClientOnly>
							</CatalogProductsWidget>
						</template>
					</div>
				</template>

				<BaseCmsRotator :fetch="{code: 'homepage_sis', limit: 18, response_fields: ['id','link_target_blank','url_without_domain','title','image_upload_path','image_thumbs','code','template']}" :ga4="'home - sis - '" @loadRotatorItems="loadRotatorItems" v-slot="{items}">
					<ClientOnly>
						<div v-if="items?.length" class="hp-sis wrapper">
							<div v-if="labels.get('homepage_sis_title') != 'homepage_sis_title'" class="hp-section-header">
								<div class="hp-title hp-sis-title"><BaseCmsLabel code="homepage_sis_title" /></div>
							</div>

							<div class="hp-sis-slider">
								<BaseUiSwiper
									name="sis"
									:options="{
										slidesPerView: 3,
										slidesPerGroup: 3,
										spaceBetween: 10,
										rewind: true,
										autoplay: {
											delay: 5000,
											disableOnInteraction: true
										},
										breakpoints: {
											1250: {
												slidesPerView: 6,
												slidesPerGroup: 6,
												spaceBetween: 25,
											},
											980: {
												slidesPerView: 6,
												slidesPerGroup: 6,
											},
											760: {
												slidesPerView: 4,
												slidesPerGroup: 4,
											},
										}
									}">
									<BaseUiSwiperSlide v-for="item in items" :key="item.id">
										<NuxtLink v-if="item.url_without_domain" :target="item.link_target_blank == 1 ? '_blank' : null" :to="item.url_without_domain" @click="gtm.gtmPromoClick(item.title, 'home - sis - ', index, item.code, item.template)" class="hp-sis-slide link">
											<span>
												<BaseUiImage :data="item.image_thumbs?.['width230-height100']" default="/images/no-image-100.webp" loading="lazy" />
											</span>
										</NuxtLink>
										<div v-else class="hp-sis-slide">
											<span>
												<BaseUiImage :data="item.image_thumbs?.['width230-height100']" default="/images/no-image-100.webp" loading="lazy" />
											</span>
										</div>
									</BaseUiSwiperSlide>
								</BaseUiSwiper>
							</div>
						</div>
					</ClientOnly>
				</BaseCmsRotator>

				<template v-if="lists?.length">
					<div class="cw wrapper" v-for="(list, index) in lists.filter(item => !item.code.startsWith('bf_'))" :key="list.id">
						<CatalogProductsWidget :fetch="{mode: 'widget', list_code: list.code, list_title: list.title, sort: 'list_position', only_available: true, limit: 20, user_use_loyalty: userLoyaltyCode}" v-slot="{items}" :mode="list.code" @loadProductsWidget="loadProductsWidget">
							<ClientOnly>
								<div v-if="items?.length" class="cw-section">
									<div class="cw-header hp-section-header">
										<div class="cw-title hp-title">{{ list.title }}</div>
										<NuxtLink class="btn btn-arrow hp-btn-d" :to="list.url_without_domain">
											<span>{{ labels.get('show_all') }}</span>
										</NuxtLink>
									</div>
									<div class="cw-tabs-content">
										<BaseUiSwiper
											:name="'cw-' + index"
											:options="{
												slidesPerView: 4,
												slidesPerGroup: 4,
												spaceBetween: 20,
												enabled: false,
												breakpoints: {
													1600: {
														slidesPerView: 5,
														slidesPerGroup: 5,
														enabled: true,
													},
													1250: {
														slidesPerView: 4,
														slidesPerGroup: 4,
														enabled: true,
													},
													980: {
														enabled: true,
													},
												}
											}">
											<BaseUiSwiperSlide v-for="(item, index) in items" :key="item.id">
												<CatalogIndexEntry :item="item" :categoryListData="list" :index="index" mode="slider" />
											</BaseUiSwiperSlide>
										</BaseUiSwiper>
									</div>
								</div>
							</ClientOnly>
						</CatalogProductsWidget>
					</div>
				</template>
			</BaseCatalogLists>

			<BaseCmsNav code="shop_in_shop" v-slot="{items}">
				<ClientOnly>
					<div v-if="items?.length" class="hp-brands wrapper">
						<div class="hp-brands-header hp-section-header">
							<div class="hp-brands-title hp-title">{{ labels.get('homepage_brands_title') }}</div>
							<BaseUtilsAppUrls v-slot="{items}">
								<NuxtLink :to="items?.manufacturer" class="btn btn-arrow">
									<span>{{ labels.get('show_all') }}</span>
								</NuxtLink>
							</BaseUtilsAppUrls>
						</div>

						<div class="hp-brands-slider">
							<BaseUiSwiper
								name="brands"
								:options="{
									slidesPerView: 3,
									slidesPerGroup: 3,
									spaceBetween: 20,
									rewind: true,
									autoplay: {
										delay: 5000,
										disableOnInteraction: true
									},
									breakpoints: {
										980: {
											slidesPerView: 6,
											slidesPerGroup: 6,
										},
									}
								}">
								<BaseUiSwiperSlide v-for="item in items" :key="item.id">
									<NuxtLink v-if="item.image" :to="item.url_without_domain" :target="item.link_target_blank == 1 ? '_blank' : null" class="hp-brands-slide">
										<BaseUiImage :data="item.image_thumbs?.['width155-height60']" default="/images/no-image-50.jpg" loading="lazy" />
									</NuxtLink>
								</BaseUiSwiperSlide>
							</BaseUiSwiper>
						</div>
					</div>
					<template #fallback>
						<BaseThemeUiLoading />
					</template>
				</ClientOnly>
			</BaseCmsNav>

			<CmsFeaturedPromo mode="extra" />

			<CmsPromo />

			<BaseCatalogLists :fetch="{code: 'special_offers', limit: 1}" v-slot="{items: lists}">
				<div v-if="lists?.length" class="hp-so">
					<div class="wrapper">
						<div class="hp-so-header">
							<div v-if="lists[0].title" class="hp-so-title hp-title">{{ lists[0].title }}</div>
							<NuxtLink :to="lists[0].external_url ? lists[0].external_url : lists[0].url_without_domain" class="btn btn-arrow"
								><span>{{ labels.get('show_all') }}</span></NuxtLink
							>
						</div>
						<div class="hp-so-cnt">
							<div v-if="lists[0].main_image" class="hp-so-promo">
								<NuxtLink :to="lists[0].external_url ? lists[0].external_url : lists[0].url_without_domain">
									<BaseUiImage
										loading="lazy"
										:data="lists[0].main_image_thumbs?.['width355-height595-crop1']"
										default="/images/no-image-100.webp"
										:picture="[{maxWidth: '760px', src: lists[0].main_image_2_thumbs?.['width760-height760']?.thumb ? lists[0].main_image_2_thumbs?.['width760-height760'].thumb : lists[0].main_image_2_thumbs?.['width760-height760']?.thumb, default: '/images/no-image-760.webp'}]" />
								</NuxtLink>
							</div>
							<BaseCatalogProductsWidget :fetch="{mode: 'widget', list_code: lists[0].code, list_title: lists[0].title, sort: 'list_position', only_available: true, limit: 9, user_use_loyalty: userLoyaltyCode}" v-slot="{items}" @loadProductsWidget="loadProductsWidget">
								<div class="hp-so-items">
									<BaseUiSwiper
										:name="'so'"
										:options="{
											slidesPerView: 3,
											slidesPerGroup: 3,
											spaceBetween: 20,
											loop: false,
											enabled: false,
											breakpoints: {
												980: {
													enabled: true,
												}
											}
										}">
										<BaseUiSwiperSlide v-for="(item, index) in items" :key="item.id">
											<CatalogIndexEntry :item="item" :categoryListData="lists[0]" :index="index" mode="slider" />
										</BaseUiSwiperSlide>
									</BaseUiSwiper>
								</div>
							</BaseCatalogProductsWidget>
						</div>
					</div>
				</div>
			</BaseCatalogLists>

			<BaseCmsRotator
				:fetch="{code: 'homepage_cards_info', limit :3, response_fields: ['id','url_without_domain','link_target_blank','title','template','image_upload_path','image_thumbs','image_2_upload_path','image_2_thumbs','content','element_button']}"
				:ga4="'home - infocards - '"
				@loadRotatorItems="loadRotatorItems"
				v-slot="{items}">
				<ClientOnly>
					<div v-if="items?.length" class="hp-info-cards hp-bg-grey">
						<div class="wrapper">
							<div class="hp-cards-section">
								<template v-for="(item, index) in items" :key="item.id">
									<NuxtLink
										v-if="item.url_without_domain"
										:to="item.url_without_domain"
										:target="item.link_target_blank == 1 ? '_blank' : null"
										class="hp-card hp-card-link"
										@click="gtm.gtmPromoClick(item.title, 'home - infocards - ', index, item.code, item.template)"
										:class="{'hp-card-medium': item.template == 'template_card_medium', 'hp-card-small': item.template == 'template_card_small'}">
										<div class="hp-cards-img" v-if="item.image_upload_path">
											<BaseUiImage v-if="item.template == 'template_card_medium'" :data="item.image_thumbs?.['width730-height540']" loading="lazy" />
											<BaseUiImage v-else :data="item.image_thumbs?.['width730-height260']" loading="lazy" />
										</div>
										<div v-if="item.image_2_upload_path || item.title || item.content || item.element_button" class="hp-cards-cnt">
											<div v-if="item.image_2_upload_path && item.template == 'template_card_medium'" class="hp-cards-logo">
												<BaseUiImage :data="item.image_2_thumbs?.['width140-height70']" loading="lazy" />
											</div>
											<div v-if="item.title" class="hp-cards-title" v-html="item.title"></div>
											<div v-if="item.content" class="hp-cards-desc" v-html="item.content"></div>
											<div v-if="item.element_button" class="hp-cards-btn btn btn-white">{{ item.element_button }}</div>
										</div>
									</NuxtLink>
									<div v-else class="hp-promo-item" :class="{'hp-card-medium': item.template == 'template_card_medium', 'hp-card-small': item.template == 'template_card_small'}">
										<div class="hp-cards-img" v-if="item.image_upload_path">
											<BaseUiImage v-if="item.template == 'template_card_medium'" :data="item.image_thumbs?.['width730-height540']" loading="lazy" />
											<BaseUiImage v-else :data="item.image_thumbs?.['width730-height260']" loading="lazy" />
										</div>
										<div v-if="item.image_2_upload_path || item.title || item.content || item.element_button" class="hp-cards-cnt">
											<div v-if="item.image_2_upload_path && item.template == 'template_card_medium'" class="hp-cards-logo">
												<BaseUiImage :data="item.image_2_thumbs?.['width140-height70']" loading="lazy" />
											</div>
											<div v-if="item.title" class="hp-cards-title" v-html="item.title"></div>
											<div v-if="item.content" class="hp-cards-desc" v-html="item.content"></div>
											<div v-if="item.element_button" class="hp-cards-btn btn btn-white">{{ item.element_button }}</div>
										</div>
									</div>
								</template>
							</div>
						</div>
					</div>
				</ClientOnly>
			</BaseCmsRotator>

			<PublishWidgetLatestPosts />
		</BaseCmsPage>
	</div>
</template>

<script setup>
	const labels = useLabels();
	const auth = useAuth();
	const gtm = useGtmBB();
	const config = useAppConfig();

	//rwd
	const {mobileBreakpoint} = inject('rwd');

	//loyalty code
	const userLoyaltyCode = computed(() => {
		const user = auth.getUser();
		return (user?.loyalty_code) ? user.loyalty_code : null;
	});

	//gtm
	function loadProductsWidget(value) {
		if(value?.items?.length) {
			gtm.viewItemList(value.items, value.data);
		}
	}

	function loadRotatorItems(value) {
		if (value?.items?.length) {
			value.items.forEach((item, index) => {
				let template = ''
				if(item?.template == 'template_card_medium') {
					template = 'medium - ';
				} else if(item?.template == 'template_card_small') {
					template = 'small - ';
				}
				gtm.viewPromotion({
					creative_name: item.title || '',
					creative_slot: value.creativeSlot + template + (index + 1),
					promotion_id: item.code || '',
					promotion_name: item.title || '',
				});
			});
		}
	}

	function onPageLoad(data) {
		if(!data) return;
		gtm.pageView({
			url: data?.url || '',
			title: data?.title || '',
		});
	}
</script>

<style lang="less" scoped>
	.hp-rotator {
		max-width: 1920px; width: 1480px; min-height: 619px; background: @white; margin: 0 auto; padding-bottom: 45px; position: relative;
		:deep(.swiper-button-prev), :deep(.swiper-button-next){margin-top: -40px;}
		.swiper:not(.swiper-initialized) .swiper-slide:not(:first-child){overflow: hidden; height: 0; visibility: hidden;}
		@media (max-width: @l) {
			width: auto; min-height: 520px; margin: 0 70px;
		}
		@media (max-width: @t) {
			width: 100%; margin: 0 auto; padding-bottom: 40px; min-height: unset;
			:deep(.swiper-button-prev), :deep(.swiper-button-next){
				width: 50px; height: 50px; margin-top: -35px; background: @white; border: none; border-radius: 100%; box-shadow: 0 20px 40px 0 rgba(1, 61, 112, 0.25);
				&:before{font-weight: 600; color: @blue;}
			}
			:deep(.swiper-button-next){right: 20px;}
			:deep(.swiper-button-prev){left: 20px;}
		}
		@media (max-width: @m) {
			padding-bottom: 30px;
		}
		@media (max-width: @ms) {
			min-height: 200px;
			padding-bottom: 25px;

			:deep(.swiper-button-prev), :deep(.swiper-button-next){
				margin-top: -15px; background: transparent;
				&:before{color: @textColor; font-weight: normal;}
				&:after{.pseudo(auto,auto); background: @white; border-radius: 100%; top: 0; bottom: 0; left: 0; right: 0; opacity: 0.5;}
			}
			:deep(.swiper-button-next){right: 15px;}
			:deep(.swiper-button-prev){left: 15px;}
		}
	}
	.hp-slider {
		min-height: 400px;

		@media (max-width: @l) {
			min-height: 300px;
		}
		@media (max-width: @t) {
			min-height: 200px;
		}
	}
	.hp-rotator-slide {
		display: flex; align-items: center; justify-content: center;
		&.link:hover .hp-rotator-img:after {
			opacity: 1;
		}
	}
	.hp-rotator-img {
		display: flex; align-items: center; justify-content: center;
		&:after {
			.pseudo(auto,auto); top: 0; left: 0; right: 0; bottom: 0; background: rgba(32, 42, 51, 0.2); opacity: 0; .transition(opacity);
		}
		:deep(img) { max-width: 100%; width: 100%; height: auto; display: block; }
	}

	:deep(.progress-bars) {
		display: flex; gap: 10px; align-items: center; justify-content: center; width: 100%; margin-top: 30px; position: relative;

		@media (max-width: @ms) {
			width: auto; margin: 10px 10px 0 10px;
		}
	}
	:deep(.progress-bar) {
		display: flex; align-items: center; height: 4px; background-color: @borderColor; transition: width 0.3s; position: relative; cursor: pointer;
		&.progress-bar-active{display: flex; background-color: #005DAC;}
		@media (min-width: @ms) {
			width: 60px!important;
		}

		@media (max-width: @ms) {
			width: auto; min-height: 3px; flex-grow: 1;
		}
	}

	.cw{
		position: relative;

		@media (max-width: @m){padding: 0 0 10px;}
	}
	.cw-section{
		margin-bottom: 40px; position: relative;

		@media (max-width: @m){margin-bottom: 30px;}
	}
	.cw-tabs-content{
		position: relative;
		:deep(.swiper-slide){width: auto; margin-right: 20px;}
		:deep(.swiper-button-prev), :deep(.swiper-button-next){top: 140px;}

		@media (max-width: @t) {
			:deep(.swiper-button-prev), :deep(.swiper-button-next){top: 90px;}
		}
		@media (max-width: @m) {
			width: calc(~'100% - -40px');margin: 0 0 0 -20px;
			:deep(.swiper-wrapper) {
				display: flex; padding: 0 0 0 20px; overflow-y: hidden; white-space: normal; position: relative;
				&::-webkit-scrollbar{display: none;}
				&::-webkit-scrollbar-thumb{display: none;}
			}
			:deep(.swiper-slide){flex-shrink: unset; width: 28%!important; min-width: 28%; margin-right: 20px; white-space: normal;}
			:deep(.swiper-navigation){display: none;}
		}
		@media (max-width: @ms) {
			width: calc(~'100% - -30px'); margin: 0 0 0 -15px;
			:deep(.swiper-wrapper){padding: 0 0 0 15px;}
			:deep(.swiper-slide){width: 40%!important; min-width: 40%;}
		}
	}
	.cw-swiper-navigation{
		@media (max-width: @m){display: none;}
	}

	.cwf{
		display: flex; flex-wrap: wrap; justify-content: space-between; position: relative; padding-bottom: 50px;

		@media (max-width: @t){padding-bottom: 30px;}
		@media (max-width: @m){display: block; padding-bottom: 15px;}
	}
	.cwf-header{
		width: 100%; margin: 0 0 20px;

		@media (max-width: @m){margin-bottom: 12px;}
	}
	.cwf-list{
		width: calc(~"50% - 20px"); margin-bottom: 40px;

		@media (max-width: @t){width: calc(~"50% - 10px"); margin-bottom: 25px;}
		@media (max-width: @m){width: 100%;}
	}
	.cwf-promo{
		height: auto; display: flex; justify-content: center; width: 140px; flex-grow: 0; flex-shrink: 0;
		a{
			display: flex; align-items: flex-start; justify-content: flex-start; flex-shrink: 0; width: 140px; z-index: 1;
			:deep(img){width: auto; height: auto; max-width: 100%; max-height: 100%; display: block; transition: box-shadow 0.3s, z-index 0.3s;}
			@media (min-width: @t){
				:deep(&:hover img){box-shadow: 0 10px 20px 0 rgba(0, 46, 86, 0.3); z-index: 11;}
			}
		}


		@media (max-width: @t){
			a:hover img{box-shadow: none;}
		}
		@media (max-width: @m){display: none;}
	}

	.cwf-tabs-content{
		display: flex; position: relative;
		:deep(.cp){display: flex; height: auto; margin-bottom: 0; padding-top: 10px;}
		:deep(.cp-image>a){
			height: 165px;
			@media (max-width: @m){height: 155px;}
		}
		:deep(.cp-cnt-comment){display: none;}
		:deep(.cp-cnt){padding: 10px;}
		:deep(.cp-badges){left: 10px;}
		:deep(.cp-badge){max-width: 100%; overflow: hidden;}
		:deep(.cp-badge>span){padding: 5px 10px;}
		:deep(.cp-badge-tooltip){display: none!important;}
		:deep(.cp-energy){left: 15px;}
		:deep(.cp-badges-special){right: 15px;}
		:deep(.cp-price){margin-top: 10px;}
		:deep(.cp-current-price){font-size: 15px;}

		:deep(.swiper-container){
			display: flex; position: relative; padding-left: 15px; flex-grow: 1; width: calc((1480px / 2) - 220px);
			@media (max-width: 1450px){
				width: calc((100vw / 2) - 220px);
			}
			@media (max-width: @m){width: 100%; padding: 0;}
		}
		:deep(.swiper-wrapper) {
			@media (max-width: @m){
				display: flex; overflow-y: hidden; white-space: normal; position: relative;
				&::-webkit-scrollbar{display: none;}
				&::-webkit-scrollbar-thumb{display: none;}
			}
		}
		:deep(.swiper){
			width: calc(100% - 65px); margin-right: 65px;
			@media (max-width: @m){width: 100%; margin-right: 0;}
		}
		:deep(.swiper-slide){
			display: flex;
			@media (max-width: @m){width: 45%!important; min-width: 140px;}
			&:hover{z-index: 111;}
			&.first-img{
				display: none;

				@media (max-width: @m){
					display: flex; align-items: flex-start; justify-content: center; flex: 0 0 120px;
					a{display: flex; align-items: flex-start; justify-content: center; width: 100%; height: 100%;}
					img{width: auto; height: auto; max-width: 100%; max-height: 100%; display: block;}
				}
			}
			&.last-img{
				display: flex; align-items: flex-start; justify-content: center; width: 140px;
				picture{display: flex; align-items: center; justify-content: center;}
				a{display: flex; align-items: flex-start; justify-content: center; width: 100%; height: 100%; .transition(opacity);}
				img{width: auto; height: auto; max-width: 100%; max-width: 100%;}
				@media (min-width: @t){
					&:hover a{opacity: 0.8;}
				}

				@media (max-width: @m){
					a{background: transparent!important;}
					img{display: block;}
				}
			}
		}
		:deep(.swiper-navigation){
			@media (max-width: @m){display: none;}
		}

		:deep(.swiper-button-prev), :deep(.swiper-button-next){
			border: 1px solid @borderColor; background: @white; left: -20px; transition: background 0.3s, border 0.3s;
			&:before{font-size: 16px; z-index: 1; .transition(color);}
			&:hover{
				background: @blue; border-color: @blue;
				&:before{color: @white;}
			}

			@media (max-width: @t){
				width: 44px; height: 44px;
				&:before{font-size: 16px;}

				&:hover{
					background: @white; border-color: @borderColor;
					&:before{color: @textColor;}
				}
			}
		}
		:deep(.swiper-button-next){left: unset; right: 0;}
		:deep(.swiper-button-disabled){display: none;}

		@media (max-width: @t){
			:deep(.cp-cnt){padding: 15px 0 10px;}
		}
		@media (max-width: @m){
			width: 100%; margin: 0; padding: 0;
			:deep(.cp-current-price){font-size: 20px;}
			:deep(.cp-badges){left: 0;}
			:deep(.cp-energy){left: 0;}
			:deep(.cp-badges-special){right: 0;}
			:deep(.cwf-slide){
				&.first, &.last{min-width: 29%; width: 29%!important; margin-right: 20px;}
			}
		}
		@media (max-width: @ms){
			:deep(.cwf-slide){
				&.first, &.last{min-width: 42%; width: 42%!important;}
			}
		}
	}

	.hp-brands{
		margin-bottom: 80px;

		@media (max-width: @l){margin-bottom: 60px;}
		@media (max-width: @t) {margin-bottom: 35px;}
		@media (max-width: @m){margin-bottom: 40px;}
	}
	.hp-brands-header{margin-bottom: 0;}
	.hp-brands-slider{
		position: relative;
		:deep(.swiper){
			display: flex; align-items: center; width: calc(~'100% - -50px'); margin-left: -25px; padding: 0 25px; position: relative;
			&:before, &:after{.pseudo(15px, auto); background: @white; top: 0; bottom: 0; left: 0; z-index: 11;}
			&:after{left: unset; right: 0;}
		}
		:deep(.swiper-wrapper){display: flex; align-items: center;}
		:deep(.swiper-slide){width: auto; margin-right: 20px; padding: 25px 0;}

		@media (max-width: @t){
			:deep(.swiper){
				width: 100%; margin: 0; padding: 15px 0;
				&:before, &:after{content: none;}
			}

			:deep(.swiper-slide){padding: 0;}
		}
	}
	.hp-brands-slide{
		height: 100%; background: @white; padding: 20px 25px; display: flex; align-items: center; justify-content: center; flex-grow: 1; text-align: center; .transition(box-shadow);
		:deep(img){width: auto; height: auto; max-width: 100%; max-height: 60px; display: block; .transition(opacity);}
		&:hover{box-shadow: 0 10px 12px 0 rgba(1, 61, 112, 0.2);}
		@media (min-width: @t){
			&:hover{border-style: none;}
		}

		@media (max-width: @t){
			&:hover{box-shadow: none;}
			:deep(img){max-height: 40px;}
		}
		@media (max-width: @ms){
			padding: 10px 15px;
			:deep(img){max-height: 30px;}
		}
	}
	.brands-swiper-navigation{
		@media (max-width: @m){display: none;}
	}

	.hp-sis{
		margin-bottom: 55px;
		:deep(.swiper-slide){width: auto; margin-right: 25px;}

		@media (max-width: @t){margin-bottom: 50px;}
		@media (max-width: @m){
			margin-bottom: 40px;
			:deep(.swiper-slide){margin-right: 10px;}
		}
	}
	.hp-sis-slider{
		position: relative;
		:deep(.swiper){
			width: calc(~'100% - -50px'); margin-left: -25px; padding: 0 25px; position: relative;
			&:before, &:after{.pseudo(15px, auto); background: @white; top: 0; bottom: 0; left: 0; z-index: 11;}
			&:after{left: unset; right: 0;}
		}

		:deep(.swiper-button-prev), :deep(.swiper-button-next){top: 30%; transform: none;}
		:deep(.swiper-button-next){.rotate(180deg);}

		@media (max-width: @t){
			:deep(.swiper){
				width: calc(~'100% - -20px'); margin-left: -10px; padding: 0 10px;
				&:before, &:after{width: 10px;}
			}
			:deep(.swiper-button-prev), :deep(.swiper-button-next){
				background: @white; border: 1px solid @borderColor; top: 13%;
				&:before{font-size: 18px; line-height: 18px;}
			}
		}
		@media (max-width: @m){
			:deep(.swiper-navigation){display: none;}
		}
	}
	.hp-sis-slide{
		display: flex; align-items: center; justify-content: center; padding: 15px 0; position: relative;
		:deep(span){display: flex; align-items: center; justify-content: center;}
		:deep(img){width: auto; height: auto; max-width: 100%; max-height: 100%; display: block; .transition(all);}
		@media (min-width: @t){
			&.link:hover :deep(img){box-shadow: 0 6px 10px 0 rgba(1,61,112,0.3);}
		}

		@media (max-width: @t){padding: 0;}
	}
	.sis-swiper-navigation{
		@media (max-width: @m){display: none;}
	}

	.hp-categories{
		margin-bottom: 80px; position: relative;

		@media (max-width: @l){margin-bottom: 60px!important;}
		@media (max-width: @t){margin-bottom: 50px!important;}
		@media (max-width: @m){margin-bottom: 35px!important;}
	}
	.hp-categories-header{width: 100%!important;}
	.hp-categories-container{
		display: grid; grid-template-columns: repeat(8, 1fr); column-gap: 30px; row-gap: 25px; margin-top: 35px;

		@media (max-width: @t){grid-template-columns: repeat(6, 1fr);}
		@media (max-width: @m){
			display: flex; width: calc(~"100% - -30px"); margin: 17px 0 0 -15px; padding: 0 15px; column-gap: unset; row-gap: unset; overflow-y: hidden; white-space: normal; position: relative;
			&::-webkit-scrollbar{display: none;}
			&::-webkit-scrollbar-thumb{display: none;}
		}
	}
	.hp-category{
		display: flex; flex-direction: column; align-items: center; font-size: 15px; line-height: 1.4; color: @textColor; text-align: center; text-decoration: none; position: relative; .transition(color);
		.img{display: flex; align-items: center; justify-content: center; flex-shrink: 0; width: 114px; height: 114px; margin-bottom: 11px; background: #F1F4F7; border: 1px solid #F1F4F7; border-radius: 100%; .transition(all);}
		:deep(img){width: auto; height: auto; max-width: 50px; max-height: 50px; display: block;}
		&:hover{
			color: @blue; text-decoration: none;
			.img{background: #FFF; border-color: #8FBADF; box-shadow: 0 5px 20px 0 rgba(0,66,123,0.08);}
		}

		@media (max-width: @t){
			&:hover{
				color: @textColor; text-decoration: none;
				.img{background: #F1F4F7; border-color: #F1F4F7;}
			}
		}
		@media (max-width: @m){
			flex-shrink: unset; width: 15%; min-width: 15%; margin-right: 25px; white-space: normal; font-size: 15px; line-height: 1.3;
			.img{width: 100%; height: 0; padding-bottom: 100%; margin-bottom: 15px; position: relative;}
			:deep(img){max-width: 50px; max-height: 50px; position: absolute; top: 50%; transform: translateY(-50%);}
		}
		@media (max-width: @ms){
			width: 26%; min-width: 26%; margin-right: 16px; font-size: 13px;
			.img{margin-bottom: 8px;}
			:deep(img){max-width: 35px; max-height: 35px;}
		}
	}

	.hp-so{
		padding: 80px 0 100px;

		@media (max-width: @t){padding: 60px 0;}
		@media (max-width: @m){padding: 30px 0;}
	}
	.hp-so-header{
		display: flex; align-items: baseline;

		@media (max-width: @t){margin-bottom: 30px;}
		@media (max-width: @m){margin-bottom: 20px;}
	}
	.hp-so-cnt{
		display: flex; margin-top: 30px;

		@media (max-width: @t){margin: 0;}
		@media (max-width: @m){display: block;}
	}
	.hp-so-promo{
		height: auto; display: flex; justify-content: center; margin-right: 50px;
		a{
			display: flex; align-items: flex-start; justify-content: flex-start; flex-shrink: 0; width: 355px; z-index: 1;
			:deep(img){
				width: auto; height: auto; max-width: 100%; max-height: 100%; display: block; .transition(box-shadow);
			}
			@media (min-width: @t){
				:deep(&:hover img){box-shadow: 0 15px 30px 0 rgba(0, 46, 86, 0.5);}
			}
		}

		@media (max-width: @l){
			a{width: 300px;}
		}
		@media (max-width: @t){
			margin-right: 20px;
			a{
				width: 225px;
				&:hover img{box-shadow: none;}
			}
		}
		@media (max-width: @m){
			width: calc(~'100% - -30px'); margin: 0 0 20px -15px;
			a{justify-content: center; flex-shrink: unset; width: 100%;}
		}
	}
	.hp-so-items{
		display: block; margin-right: 55px; width: calc(~"100% - 460px"); position: relative;

		:deep(.cp){height: auto; margin-bottom: 0;}
		:deep(.cp-image>a){
			height: 265px;

			@media (max-width: @l){height: 215px;}
			@media (max-width: @t){height: 205px;}
		}

		:deep(.swiper){display: flex; height: 100%;}
		:deep(.swiper-slide){display: flex; width: auto; margin-right: 20px;}
		:deep(.so-swiper-navigation){
			display: flex; flex-direction: column-reverse; position: absolute; top: 0; right: -55px;

			@media (max-width: @t){
				flex-direction: row; top: -74px; right: 0;
			}
		}
		:deep(.swiper-button-prev), :deep(.swiper-button-next){
			border: 1px solid @borderColor; transform: none; background: @white; top: unset; bottom: unset; left: unset; right: unset; position: relative; transition: background 0.3s, border 0.3s;
			&:before{font-size: 16px; z-index: 1; .transition(color);}
			&:hover{
				background: @blue; border-color: @blue;
				&:before{color: @white;}
			}

			@media (max-width: @t){
				width: 44px; height: 44px;
				&:before{font-size: 16px;}

				&:hover{
					background: @white; border-color: @borderColor;
					&:before{color: @textColor;}
				}
			}
		}
		:deep(.swiper-button-prev){
			margin-top: 10px;

			@media (max-width: @t){margin: 0 10px 0 0;}
		}
		:deep(.swiper-button-next){.rotate(180deg);}
		:deep(.swiper-button-disabled){
			&:hover{
				background: @white; border-color: @borderColor;
				&:before{color: @textColor;}
			}
		}

		@media (max-width: @l){margin-right: 0; width: calc(~"100% - 405px");}
		@media (max-width: @t){width: calc(~"100% - 245px");}
		@media (max-width: @m){
			width: calc(~'100% - -40px'); margin-left: -20px;
			:deep(.swiper-wrapper) {
				display: flex; padding: 0 0 0 20px; overflow-y: hidden; white-space: normal; position: relative;
				&::-webkit-scrollbar{display: none;}
				&::-webkit-scrollbar-thumb{display: none;}
			}
			:deep(.swiper-slide){flex-shrink: unset; width: 30%!important; min-width: 30%; margin-right: 20px; white-space: normal;}
			:deep(.swiper-navigation){display: none;}
		}
		@media (max-width: @ms){
			width: calc(~'100% - -30px'); margin-left: -15px;
			:deep(.swiper-wrapper){padding: 0 0 0 15px!important;}
			:deep(.swiper-slide){width: 42%!important; min-width: 42%;}
		}
	}

	.hp-info-cards {
		padding: 50px 0;

		@media (max-width: @t) {
			padding: 35px 0;
		}
		@media (max-width: @m) {
			padding: 20px 0;
		}
		@media (max-width: @ms) {
			padding: 0;
			background: @white;
		}
	}
	.hp-cards-section {
		display: flex;
		flex-wrap: wrap;
		flex-direction: column;
		width: calc(~'100% - -20px');
		max-height: 560px;
		margin-left: -10px;

		@media (max-width: @t) {
			width: calc(~'100% - -10px');
			max-height: 460px;
			margin-left: -5px;
		}
		@media (max-width: @m) {
			max-height: 350px;
		}
		@media (max-width: @ms) {
			display: block;
			width: calc(~'100% - -30px');
			max-height: unset;
			margin-left: -15px;
		}
	}
	.hp-card {
		display: block;
		width: calc(~'48% - -10px');
		position: relative;
		color: @white;
		margin: 10px;
		:deep(img) {
			width: auto;
			height: auto;
			max-width: 100%;
			max-height: 100%;
			display: block;
		}
		&:hover {
			color: @white;
		}

		@media (max-width: @t) {
			width: calc(~'48% - -5px');
			margin: 7px 5px;
		}
		@media (max-width: @m) {
			margin: 5px;
		}
		@media (max-width: @ms) {
			width: 100%;
			margin: 0 0 1px;
		}
	}
	.hp-cards-img :deep(span) {
		display: block;
	}
	.hp-cards-cnt {
		position: absolute;
		top: 0;
		left: 0;
		bottom: 0;
		width: 50%;
		padding: 75px 10px 75px 80px;

		@media (max-width: @t) {
			width: 50%;
			padding: 60px 10px 10px 30px;
		}
		@media (max-width: @m) {
			width: 55%;
			padding: 40px 10px 10px 20px;
		}
		@media (max-width: @ms) {
			width: 55%;
			padding: 40px 0 10px 15px;
			top: 0;
		}
	}
	.hp-cards-logo {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 30px;
		max-width: 250px;
		max-height: 140px;
		:deep(img) {
			width: auto;
			height: auto;
			max-width: 100%;
			max-height: 100%;
			display: block;
		}

		@media (max-width: @t) {
			margin-bottom: 15px;
			max-width: 120px;
			max-height: 60px;
		}
		@media (max-width: @m) {
			margin-bottom: 13px;
			max-width: 100px;
			max-height: 45px;
		}
		@media (max-width: @ms) {
			margin-bottom: 10px;
			max-width: 60px;
			max-height: 30px;
		}
	}
	.hp-cards-title {
		font: 26px/1.3 @font;
		letter-spacing: -0.5px;

		@media (max-width: @l) {
			font-size: 20px;
		}
		@media (max-width: @t) {
			font-size: 20px;
		}
		@media (max-width: @m) {
			font-size: 18px;
		}
		@media (max-width: @ms) {
			font-size: 20px;
			line-height: 24px;
		}
	}
	.hp-cards-desc {
		font-size: 16px;
		line-height: 1.4;
		letter-spacing: -0.3px;
		margin-top: 8px;

		@media (max-width: @l) {
			font-size: 12px;
		}
		@media (max-width: @t) {
			font-size: 14px;
		}
		@media (max-width: @m) {
			font-size: 14px;
			margin-top: 5px;
		}
		@media (max-width: @ms) {
			font-size: 14px;
			line-height: 19px;
			margin-top: 5px;
		}
	}
	.hp-cards-btn {
		min-width: 215px;
		background: transparent;
		color: @white;
		font-weight: normal;
		margin-top: 35px;
		border: 1px solid @white;
		.transition(all);
		&:hover {
			border: 1px solid @white;
			background: @white;
			color: @textColor;
		}

		@media (max-width: @l) {
			min-width: 170px;
			height: 43px;
			font-size: 14px;
			margin-top: 30px;
		}
		@media (max-width: @t) {
			min-width: 180px;
			margin-top: 12px;
			&:hover {
				background: transparent;
				box-shadow: none;
				color: @white;
			}
		}
		@media (max-width: @m) {
			min-width: 160px;
			height: 44px;
			margin-top: 12px;
			font-size: 14px;
		}
		@media (max-width: @ms) {
			display: inline-flex;
			min-width: 150px;
			height: 44px;
			margin-top: 15px;
		}
	}
	.hp-card-small {
		max-height: 48%;
		.hp-cards-cnt {
			padding: 10px 10px 10px 60px;
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: flex-start;
		}
		.hp-cards-btn {
			min-width: 145px;
			margin-top: 20px;
		}

		@media (max-width: @l) {
			.hp-cards-cnt {
				padding: 10px 10px 10px 50px;
			}
			.hp-cards-btn {
				min-width: 120px;
			}
		}
		@media (max-width: @t) {
			max-height: 48%;
			.hp-cards-cnt {
				padding: 10px 10px 10px 25px;
			}
			.hp-cards-btn {
				min-width: 140px;
				height: 44px;
				margin-top: 10px;
			}
		}
		@media (max-width: @m) {
			.hp-cards-cnt {
				padding: 5px 5px 5px 15px;
			}
			.hp-cards-title {
				font-size: 16px;
			}
			.hp-cards-desc {
				font-size: 12px;
				margin-top: 5px;
			}
			.hp-cards-btn {
				min-width: 120px;
				height: 40px;
			}
		}
		@media (max-width: @ms) {
			max-height: unset;
			.hp-cards-cnt {
				padding: 15px 0 15px 15px;
			}
			.hp-cards-title {
				font-size: 16px;
				line-height: 21px;
			}
			.hp-cards-desc {
				font-size: 11px;
				line-height: 15px;
			}
			.hp-cards-btn {
				min-width: 110px;
				margin-top: 8px;
			}
		}
	}
</style>

<style lang="less">
	.hp-bg-grey {
		background: #f0f2f4;
	}

	.hp-section-header {
		margin-bottom: 10px;

		@media (max-width: @t) {
			margin-bottom: 20px;
		}
		@media (max-width: @m) {
			margin-bottom: 15px;
		}
		@media (max-width: @ms) {
			margin-bottom: 12px;
		}
	}
	.hp-title {
		display: inline;
		margin-right: 7px;
		font: 30px/1.2 @font;
		color: @textColor;
		font-weight: 600;

		@media (max-width: @t) {
			font-size: 26px;
		}
		@media (max-width: @m) {
			font-size: 22px;
			margin-right: 6px;
		}
	}
</style>
